import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type DailyNoteCategoryOption = {
  id: string;
  name: string;
  color: string;
};

function categoryDotStyle(
  name: string,
  color: string,
  isDefaultName: (n: string) => boolean
): React.CSSProperties {
  const isWhite = color.toLowerCase() === '#ffffff';
  return {
    background: color,
    border:
      isDefaultName(name) || isWhite
        ? '1px solid color-mix(in srgb, var(--color-border) 78%, transparent)'
        : 'none',
  };
}

type Props = {
  categories: DailyNoteCategoryOption[];
  value: string | null;
  onChange: (categoryId: string) => void;
  disabled?: boolean;
  ariaLabel: string;
  variant?: 'field' | 'pill';
  isDefaultName?: (name: string) => boolean;
};

const DailyNoteCategoryDropdown: React.FC<Props> = ({
  categories,
  value,
  onChange,
  disabled = false,
  ariaLabel,
  variant = 'field',
  isDefaultName = () => false,
}) => {
  const selected = categories.find((c) => c.id === value) ?? null;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      const anchor = wrapRef.current;
      const menu = menuRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const minW = variant === 'pill' ? 140 : 160;
      const menuW = Math.max(rect.width, menu?.offsetWidth || minW);
      const menuH = menu?.offsetHeight || 180;
      const gap = 4;
      const margin = 8;
      const left = Math.min(Math.max(margin, rect.left), window.innerWidth - menuW - margin);
      let top = rect.bottom + gap;
      if (top + menuH > window.innerHeight - margin) {
        top = Math.max(margin, rect.top - menuH - gap);
      }
      setPos({ top, left, width: Math.max(rect.width, minW) });
    };
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, categories.length, variant]);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || categories.length === 0) return;
    setOpen((v) => !v);
  };

  const menu =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            className="cp-todo-note-row-category-menu cp-todo-note-row-category-menu--portal"
            role="listbox"
            aria-label={ariaLabel}
            style={
              pos
                ? { top: pos.top, left: pos.left, width: pos.width }
                : { top: -9999, left: -9999, visibility: 'hidden' }
            }
            onMouseDown={(e) => e.stopPropagation()}
          >
            {categories.map((c) => {
              const isActive = value === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`cp-todo-note-row-category-option${isActive ? ' is-active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(c.id);
                    setOpen(false);
                  }}
                >
                  <span
                    className="cp-todo-note-chip-dot"
                    style={categoryDotStyle(c.name, c.color, isDefaultName)}
                    aria-hidden
                  />
                  <span>{c.name}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )
      : null;

  if (variant === 'pill') {
    return (
      <div
        ref={wrapRef}
        className={'cp-dash-weekly-sticky-cat-wrap' + (selected ? '' : ' is-placeholder')}
      >
        <button
          type="button"
          className={'cp-dash-weekly-sticky-cat' + (selected ? '' : ' is-placeholder')}
          disabled={disabled || categories.length === 0}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={toggle}
        >
          {selected ? selected.name : '\u00a0'}
        </button>
        {menu}
      </div>
    );
  }

  return (
    <div className="cp-daily-note-editor-cat">
      <span className="cp-daily-note-editor-cat-label">카테고리</span>
      <div ref={wrapRef} className="cp-todo-note-cat-cell-inner cp-daily-note-editor-cat-field">
        <button
          type="button"
          className="cp-daily-note-editor-cat-trigger"
          disabled={disabled || categories.length === 0}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={toggle}
        >
          {selected ? (
            <>
              <span
                className="cp-todo-note-chip-dot cp-todo-note-cat-field-dot"
                style={categoryDotStyle(selected.name, selected.color, isDefaultName)}
                aria-hidden
              />
              <span className="cp-daily-note-editor-cat-name">{selected.name}</span>
            </>
          ) : (
            <span className="cp-daily-note-editor-cat-name is-placeholder">선택</span>
          )}
        </button>
        <button
          type="button"
          className="cp-todo-note-cat-picker-btn"
          disabled={disabled || categories.length === 0}
          aria-label="카테고리 목록에서 선택"
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={toggle}
        >
          <svg className="cp-todo-note-cat-picker-icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden>
            <path fill="currentColor" d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z" />
          </svg>
        </button>
        {menu}
      </div>
    </div>
  );
};

export default DailyNoteCategoryDropdown;
